// Puerto verbatim de packages/hce/src/hce/auditedFetch.ts — infraestructura
// regulatoria, no un detalle de implementación (ver contexto del plan del
// proyecto): aplica Modo Consulta
// (solo-lectura por licencia impaga) y bloqueo duro de recursos firmados, y
// da un paliativo de caché/outbox cuando no hay red. Si esta app arma su
// propio MedplumClient sin este wrapper, pierde todo esto en silencio.
//
// MT-050: la bitácora de acceso a la historia clínica YA NO la escribe este
// archivo. La escribe el proxy del servidor (lib/medplum-proxy.ts +
// lib/hce-proxy-bitacora.ts) con usuario, paciente, recurso, método, módulo e
// IP, de modo que una llamada directa con el token de sesión también queda
// anotada. Aquí solo viaja la cabecera `X-LC-Modulo` (la pantalla de origen)
// y se sigue reportando la copia de historia (`hce-record-copy`), que no es
// una petición FHIR.
import { formatHumanName } from '@medplum/core';
import type { MedplumClient } from '@medplum/core';
import type { Resource } from '@medplum/fhirtypes';
import { getConfig } from '../config';
import { estaBloqueado } from './bloqueo';
import { esSoloLectura, obtenerEstadoLicenciaHce } from './modoConsulta';
import { moduloActual } from './modulo';
import { encolarEscritura, vaciarOutbox } from './outbox';
import { guardarEnCache, leerDeCache } from './readCache';

const TENANT_SLUG_SYSTEM = 'http://logiacare.ec/fhir/tenant-slug';

let medplumActual: MedplumClient | undefined;
let tenantSlugCache: string | null | undefined;

export function registrarMedplumParaAuditoria(client: MedplumClient): void {
  medplumActual = client;
}

export function _resetAuditStateForTests(): void {
  medplumActual = undefined;
  tenantSlugCache = undefined;
}

function tipoDeRecurso(url: string): string | undefined {
  return /\/fhir\/R4\/([A-Za-z]+)/.exec(url)?.[1];
}

async function resolverTenantSlug(): Promise<string | null> {
  if (tenantSlugCache !== undefined) {
    return tenantSlugCache;
  }
  if (!medplumActual) {
    return null;
  }
  try {
    const bundle = await medplumActual.search('Organization', {});
    const org = bundle.entry?.[0]?.resource;
    const slug = org?.identifier?.find((i) => i.system === TENANT_SLUG_SYSTEM)?.value;
    tenantSlugCache = slug ?? null;
  } catch {
    tenantSlugCache = null;
  }
  return tenantSlugCache;
}

// MT-050: lecturas y escrituras FHIR las anota el servidor (medplum-proxy.ts); desde el navegador solo se reporta la copia de historia (no es una petición FHIR).
type AccionAuditoria = 'hce-record-copy';

async function reportarAcceso(accion: AccionAuditoria, targetRef: string, detalle?: string): Promise<boolean> {
  const config = getConfig();
  if (!config.vaultUrl) {
    return false;
  }
  const tenantSlug = await resolverTenantSlug();
  if (!tenantSlug) {
    return false;
  }
  try {
    const profile = medplumActual?.getProfile();
    const practitionerName = profile ? formatHumanName(profile.name?.[0]) : undefined;
    const res = await fetch(`${config.vaultUrl}/api/hce/audit`, {
      method: 'POST',
      headers: cabecerasBoveda(),
      body: JSON.stringify({ tenantSlug, accion, practitionerName, targetRef, detalle }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export function cabecerasBoveda(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...extra };
  const token = medplumActual?.getAccessToken?.();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

export async function reportarAccesoManual(accion: AccionAuditoria, targetRef: string, detalle?: string): Promise<boolean> {
  return reportarAcceso(accion, targetRef, detalle);
}

export async function obtenerTenantSlug(): Promise<string | null> {
  return resolverTenantSlug();
}

// MT-050: lo creado sin conexión se vuelve a crear contra el proxy al reconectar, y el proxy anota esa creación en la bitácora del servidor. Ya no hay nada
// que reportar desde el navegador (la etiqueta `auditado-diferido` del propio recurso conserva que nació sin conexión).
async function reportarAccesoDiferido(_resourceType: string, _targetRef: string, _creadoEn: string): Promise<void> {}

export async function sincronizarPendientes(): Promise<{ sincronizados: number; fallidos: number } | undefined> {
  if (!medplumActual) return undefined;
  return vaciarOutbox(medplumActual, reportarAccesoDiferido);
}

function respuestaJson(cuerpo: unknown, status: number): Response {
  return new Response(JSON.stringify(cuerpo), { status, headers: { 'content-type': 'application/fhir+json' } });
}

export function crearFetchAuditado(): typeof fetch {
  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = typeof input === 'string' ? input : input.toString();
    const resourceType = tipoDeRecurso(url);
    const method = (init?.method ?? 'GET').toUpperCase();
    const esLectura = method === 'GET' || method === 'HEAD';

    if (resourceType && method !== 'GET' && method !== 'HEAD') {
      const estadoLicencia = await obtenerEstadoLicenciaHce(medplumActual);
      if (esSoloLectura(estadoLicencia)) {
        throw new Error('Este consultorio está en Modo Consulta (solo lectura) por falta de pago — no se pueden guardar cambios. Contacta a soporte para reactivar la licencia.');
      }
    }

    if (resourceType && (method === 'PUT' || method === 'PATCH')) {
      let cuerpo: { authored?: string } | undefined;
      try {
        cuerpo = init?.body ? (JSON.parse(String(init.body)) as { authored?: string }) : undefined;
      } catch {
        cuerpo = undefined;
      }
      if (await estaBloqueado(medplumActual, { resourceType, authored: cuerpo?.authored })) {
        throw new Error(`${resourceType} es inmutable — usa una adenda (Provenance) en vez de editarlo.`);
      }
    }

    // MT-050: la bitácora de acceso la escribe el SERVIDOR (proxy a Medplum). El navegador solo declara desde qué pantalla sale la petición.
    let initConModulo = init;
    if (resourceType) {
      const cabeceras = new Headers(init?.headers);
      cabeceras.set('X-LC-Modulo', moduloActual());
      initConModulo = { ...init, headers: cabeceras };
    }

    let response: Response;
    try {
      response = await fetch(input, initConModulo);
    } catch (errorDeRed) {
      if (!resourceType) throw errorDeRed;

      if (esLectura) {
        const cache = await leerDeCache(url);
        if (cache) return respuestaJson(cache.cuerpo, 200);
        throw errorDeRed;
      }
      if (method === 'POST') {
        const cuerpo = init?.body ? (JSON.parse(String(init.body)) as Resource) : undefined;
        if (cuerpo?.resourceType === resourceType) {
          const local = await encolarEscritura(resourceType, cuerpo);
          return respuestaJson(local, 201);
        }
      }
      throw errorDeRed;
    }

    if (!resourceType || !response.ok) {
      return response;
    }

    if (esLectura) {
      response
        .clone()
        .json()
        .then((cuerpo) => guardarEnCache(url, cuerpo))
        .catch(() => {});
    }

    return response;
  };
}
